import type { BoardNode, Connection, TileType } from './types';

/**
 * Board graph engine.
 *
 * Invariants:
 * - Every node ID is unique within the graph.
 * - Every connection's targetId references a node that exists in the graph.
 * - The graph is immutable after construction (built from BoardNode[] in constructor).
 *
 * Board JSON stores authored outgoing connections. Normal walk connections are authored in
 * both directions, while movement filters the immediate backtrack edge at runtime.
 * Spec §3: "Forks: player chooses direction when multiple outgoing connections exist."
 * Spec §3: "No dead ends. Looping paths."
 */
export class BoardGraph {
  private readonly nodes: Map<string, BoardNode>;

  /**
   * Construct a BoardGraph from an array of BoardNode.
   * Throws on duplicate IDs or dangling connections.
   */
  constructor(nodes: BoardNode[]) {
    this.nodes = new Map<string, BoardNode>();

    // First pass: build the map, detecting duplicate IDs
    for (const node of nodes) {
      if (this.nodes.has(node.id)) {
        throw new Error(`Duplicate node ID: ${node.id}`);
      }
      this.nodes.set(node.id, node);
    }

    // Second pass: verify all connection targets exist
    for (const node of this.nodes.values()) {
      for (const conn of node.connections) {
        if (!this.nodes.has(conn.targetId)) {
          throw new Error(
            `Dangling connection: node ${node.id} references non-existent target ${conn.targetId}`,
          );
        }
      }
    }
  }

  /** Get a node by ID. Returns undefined if not found. */
  getNode(id: string): BoardNode | undefined {
    return this.nodes.get(id);
  }

  /** Get all nodes in the graph. Returns a new array (defensive copy). */
  getAllNodes(): BoardNode[] {
    return Array.from(this.nodes.values());
  }

  /** Get the total number of nodes. */
  get size(): number {
    return this.nodes.size;
  }

  /**
   * Get outgoing neighbors of a node (following its connections).
   * Returns array of {node, connectionType} pairs.
   * Returns empty array if node not found or has no connections.
   */
  getNeighbors(nodeId: string): Array<{ node: BoardNode; connectionType: Connection['type'] }> {
    const node = this.nodes.get(nodeId);
    if (!node) return [];

    const result: Array<{ node: BoardNode; connectionType: Connection['type'] }> = [];
    for (const conn of node.connections) {
      const target = this.nodes.get(conn.targetId);
      if (target) {
        result.push({ node: target, connectionType: conn.type });
      }
    }
    return result;
  }

  /**
   * Check if a node is a fork (has more than one outgoing connection).
   * Spec §3: "Forks: player chooses direction when multiple outgoing connections exist."
   * Returns false for missing node.
   */
  isFork(nodeId: string): boolean {
    const node = this.nodes.get(nodeId);
    if (!node) return false;
    return node.connections.length > 1;
  }

  /**
   * Get all nodes of a specific tile type.
   * Useful for finding all graveyards, treasure chests, etc.
   */
  getNodesByType(type: TileType): BoardNode[] {
    const result: BoardNode[] = [];
    for (const node of this.nodes.values()) {
      if (node.type === type) {
        result.push(node);
      }
    }
    return result;
  }

  /**
   * Get all nodes that support goals (supportsGoal === true).
   * Used by treasure chest / weapons cache placement logic.
   */
  getGoalNodes(): BoardNode[] {
    const result: BoardNode[] = [];
    for (const node of this.nodes.values()) {
      if (node.supportsGoal) {
        result.push(node);
      }
    }
    return result;
  }

  /**
   * Validate graph invariants:
   * - No duplicate node IDs
   * - All connection targetIds reference existing nodes
   * - Normal walk connections are bidirectional
   * - Starting tiles have outgoing walk edges only
   * - No dead ends (every non-pathing node has at least one outgoing walk connection)
   * Returns array of error strings (empty = valid).
   */
  validate(): string[] {
    const errors: string[] = [];
    const seenIds = new Set<string>();

    for (const node of this.nodes.values()) {
      // Check for duplicate IDs (defensive — constructor should prevent this,
      // but validate() must independently verify the invariant)
      if (seenIds.has(node.id)) {
        errors.push(`Duplicate node ID: ${node.id}`);
      }
      seenIds.add(node.id);

      let outgoingWalkCount = 0;

      // Check for dangling connections
      for (const conn of node.connections) {
        const target = this.nodes.get(conn.targetId);
        if (!target) {
          errors.push(
            `Dangling connection: node ${node.id} references non-existent target ${conn.targetId}`,
          );
          continue;
        }

        if (conn.type === 'walk') outgoingWalkCount++;

        if (conn.type === 'walk' && target.type === 'start') {
          errors.push(`Invalid start edge: node ${node.id} has incoming walk edge to ${target.id}`);
        }

        if (
          conn.type === 'walk' &&
          node.type !== 'start' &&
          !target.connections.some(
            (reverse) => reverse.type === 'walk' && reverse.targetId === node.id,
          )
        ) {
          errors.push(`Missing reverse walk edge: node ${node.id} -> ${target.id}`);
        }
      }

      // Check for dead ends: non-pathing nodes must have at least one outgoing walk edge
      if (node.type !== 'pathing' && outgoingWalkCount === 0) {
        errors.push(
          `Dead end: node ${node.id} (type: ${node.type}) has no outgoing walk connections`,
        );
      }
    }

    return errors;
  }
}
